import React from 'react';
import { ArrowLeft, FileText, AlertTriangle, CheckCircle, XCircle, Scale } from 'lucide-react';
import { Link } from 'react-router-dom';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link 
                to="/"
                className="flex items-center gap-2 text-gray-600 hover:text-orange-600 transition-colors duration-200"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Home</span>
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="w-6 h-6 text-orange-600" />
              <h1 className="text-xl font-bold text-gray-800">Terms of Service</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-8 py-12 text-white">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <FileText className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-4xl font-bold mb-2">Terms of Service</h2>
                <p className="text-blue-100 text-lg">
                  Last updated: {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
            <p className="text-blue-100 text-lg leading-relaxed">
              Please read these terms carefully before using our platform. By using our services, you agree to these terms.
            </p>
          </div>
          
          {/* Content Section */}
          <div className="p-8">
            <div className="space-y-8">
              {/* Introduction */}
              <section className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-100">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800">Introduction</h3>
                </div>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Welcome to EventPlots. These Terms of Service ("Terms") govern your use of our event ticketing platform and related services. By accessing or using our platform, you agree to be bound by these Terms and our Privacy Policy.
                </p>
              </section>

              {/* Acceptance of Terms */}
              <section>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Acceptance of Terms</h3>
                <p className="text-gray-700 leading-relaxed">
                  By creating an account, purchasing tickets, or using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree to these Terms, please do not use our platform.
                </p>
              </section>

              {/* Platform Description */}
              <section>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Platform Description</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  EventPlots is an online event ticketing platform that allows:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Event organizers to create and manage events</li>
                  <li>Users to discover and purchase event tickets</li>
                  <li>Secure payment processing and ticket delivery</li>
                  <li>Event management and analytics tools</li>
                  <li>Customer support and communication features</li>
                </ul>
              </section>

              {/* User Accounts */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <h3 className="text-xl font-semibold text-gray-800">User Accounts</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Account Creation</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                      <li>You must provide accurate and complete information</li>
                      <li>You are responsible for maintaining account security</li>
                      <li>You must be at least 18 years old to create an account</li>
                      <li>One account per person is allowed</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Account Responsibilities</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                      <li>Keep your login credentials secure</li>
                      <li>Notify us immediately of any unauthorized access</li>
                      <li>Update your information when it changes</li>
                      <li>Comply with all applicable laws and regulations</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Ticket Purchases */}
              <section>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Ticket Purchases</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Purchase Terms</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                      <li>All ticket sales are final unless otherwise stated</li>
                      <li>Prices are subject to change without notice</li>
                      <li>Service fees may apply to all transactions</li>
                      <li>Payment must be completed before ticket delivery</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Refund Policy</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                      <li>Refunds are subject to event organizer policies</li>
                      <li>Service fees are generally non-refundable</li>
                      <li>Cancelled events may be eligible for full refunds</li>
                      <li>Refund requests must be submitted within specified timeframes</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Prohibited Activities */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <XCircle className="w-6 h-6 text-red-600" />
                  <h3 className="text-xl font-semibold text-gray-800">Prohibited Activities</h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  You agree not to engage in any of the following activities:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Creating fake events or fraudulent listings</li>
                  <li>Reselling tickets at inflated prices (scalping)</li>
                  <li>Using automated systems to purchase tickets</li>
                  <li>Attempting to circumvent security measures</li>
                  <li>Harassing or threatening other users</li>
                  <li>Violating intellectual property rights</li>
                  <li>Distributing malware or harmful code</li>
                  <li>Interfering with platform functionality</li>
                </ul>
              </section>

              {/* Event Organizer Responsibilities */}
              <section>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Event Organizer Responsibilities</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Event Information</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                      <li>Provide accurate and complete event details</li>
                      <li>Maintain appropriate event capacity and safety</li>
                      <li>Comply with all local laws and regulations</li>
                      <li>Obtain necessary permits and licenses</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Customer Service</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                      <li>Respond to attendee inquiries promptly</li>
                      <li>Handle refund requests according to policies</li>
                      <li>Provide clear event cancellation procedures</li>
                      <li>Ensure event accessibility and accommodations</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Payment and Fees */}
              <section>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Payment and Fees</h3>
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    All payments are processed securely through our payment partners. Fees may include:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                    <li><strong>Service Fees:</strong> Platform usage and processing fees</li>
                    <li><strong>Payment Processing:</strong> Credit card and bank transfer fees</li>
                    <li><strong>Currency Conversion:</strong> Fees for international transactions</li>
                    <li><strong>Taxes:</strong> Applicable sales and value-added taxes</li>
                  </ul>
                </div>
              </section>

              {/* Intellectual Property */}
              <section>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Intellectual Property</h3>
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Our platform, including its content, design, and functionality, is protected by intellectual property laws:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                    <li>EventPlots retains all rights to platform content and design</li>
                    <li>Users retain rights to their event content and materials</li>
                    <li>Unauthorized use of our trademarks is prohibited</li>
                    <li>Content must not violate third-party intellectual property rights</li>
                  </ul>
                </div>
              </section>

              {/* Limitation of Liability */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-yellow-600" />
                  <h3 className="text-xl font-semibold text-gray-800">Limitation of Liability</h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  EventPlots provides the platform "as is" and makes no warranties about:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Platform availability or uninterrupted service</li>
                  <li>Accuracy of event information provided by organizers</li>
                  <li>Security of third-party payment processors</li>
                  <li>Compatibility with all devices and browsers</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  Our liability is limited to the amount paid for tickets in the 12 months preceding any claim.
                </p>
              </section>

              {/* Dispute Resolution */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <Scale className="w-6 h-6 text-blue-600" />
                  <h3 className="text-xl font-semibold text-gray-800">Dispute Resolution</h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  In the event of disputes, we encourage resolution through:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Direct communication with our customer support team</li>
                  <li>Mediation services for complex disputes</li>
                  <li>Legal proceedings in courts of competent jurisdiction</li>
                  <li>Arbitration for certain types of claims</li>
                </ul>
              </section>

              {/* Termination */}
              <section>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Account Termination</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We may terminate or suspend your account for:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Violation of these Terms of Service</li>
                  <li>Fraudulent or illegal activities</li>
                  <li>Repeated policy violations</li>
                  <li>Extended periods of inactivity</li>
                </ul>
              </section>

              {/* Changes to Terms */}
              <section>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Changes to Terms</h3>
                <p className="text-gray-700 leading-relaxed">
                  We reserve the right to modify these Terms at any time. We will notify users of material changes through our platform or email. Continued use of our services after changes constitutes acceptance of the updated Terms.
                </p>
              </section>

              {/* Governing Law */}
              <section>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Governing Law</h3>
                <p className="text-gray-700 leading-relaxed">
                  These Terms are governed by the laws of Uganda. Any disputes shall be resolved in the courts of Kampala, Uganda, unless otherwise specified in these Terms.
                </p>
              </section>

              {/* Contact Information */}
              <section>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Contact Us</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have questions about these Terms of Service, please contact us:
                </p>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700">
                    <strong>Email:</strong> legal@eventplots.com<br />
                    <strong>Phone:</strong> +256 XXX XXX XXX<br />
                    <strong>Address:</strong> Kampala, Uganda<br />
                    <strong>Response Time:</strong> We aim to respond within 48 hours
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms; 